import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Tooltip, Button } from '@mantine/core';
import { IconReceipt, IconCashBanknoteMove, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { formatPrice } from '@/lib/format-utils';
import { Reservation } from '@turizi/shared';
import ReservationPriceModifiers from './reservation-price-modifiers';
import ReservationPriceModifiersView from './reservation-price-modifiers-view';
import ReservationCurrencyExchangeModal from './reservation-currency-exchange-modal';

interface Props {
    reservation: Reservation;
    size?: 'sm' | 'md' | 'lg' | 'xs';
    className?: string;
    showStatus?: boolean;
    disabled?: boolean;
    showServiceDetails?: boolean;
    showModifiers?: boolean;
}

export default function ReservationPriceTotals({
    reservation,
    size = 'sm',
    className = '',
    disabled = false,
    showServiceDetails = true,
    showModifiers = true,
}: Props) {
    const { t } = useTranslation();
    const [exchangeModalOpened, setExchangeModalOpened] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const bookings = reservation?.service_bookings || [];
    const totalMoneyInByCurrency = reservation?.price_per_currency || {};
    const owedByCurrency = reservation?.payment_owed_per_currency || {};
    const modifiers = reservation?.price_modifiers || [];

    // Compute base subtotals per currency directly from service bookings
    const bookingsSubtotalPerCurrency: Record<string, number> = {};
    bookings.forEach((b) => {
        const curr = b.money_in_currency || '**';
        const amt = Number(b.money_in_amount || 0);
        bookingsSubtotalPerCurrency[curr] = (bookingsSubtotalPerCurrency[curr] || 0) + amt;
    });

    // Group bookings by category and currency
    const groupedBookingsMap = new Map<string, {
        categoryKey: string;
        categoryLabel: string;
        currency: string;
        totalAmount: number;
        totalPax: number;
        count: number;
    }>();

    bookings.forEach((booking) => {
        const catKey = booking.category || booking.service?.category || 'service';
        const curr = booking.money_in_currency || '**';
        const groupKey = `${catKey}__${curr}`;

        const groupPaxCount = (booking.pax_groups || []).reduce((acc, bg) => {
            return acc + ((bg.adults || 0) + (bg.children || 0) + (bg.seniors || 0) + (bg.infants || 0) + (bg.guests || 0));
        }, 0);
        const paxCount = (booking.adults || 0) + (booking.children || 0) + (booking.infants || 0) + (booking.seniors || 0) || groupPaxCount;
        const amt = Number(booking.money_in_amount || 0);

        if (!groupedBookingsMap.has(groupKey)) {
            const rawLabel = booking.category ? t(booking.category) : (booking.service?.name || t('service_booking'));
            groupedBookingsMap.set(groupKey, {
                categoryKey: catKey,
                categoryLabel: rawLabel,
                currency: curr,
                totalAmount: 0,
                totalPax: 0,
                count: 0,
            });
        }

        const group = groupedBookingsMap.get(groupKey)!;
        group.totalAmount += amt;
        group.totalPax += paxCount;
        group.count += 1;
    });

    const groupedBookings = Array.from(groupedBookingsMap.values());

    const activeCurrencies = Object.keys(totalMoneyInByCurrency).length > 0
        ? Object.keys(totalMoneyInByCurrency)
        : Object.keys(bookingsSubtotalPerCurrency);

    const currencies = Array.from(new Set(activeCurrencies));

    if (currencies.length === 0 && bookings.length === 0) {
        return null;
    }

    const status = reservation?.payment_status || '--';
    const isTicketView = size === 'md' || size === 'lg';

    if (isTicketView) {
        return (
            <div className={`space-y-3 font-sans ${className}`}>
                {/* 1. Ticket Header */}
                {showServiceDetails && (
                    <>
                        <div
                            className="flex items-center justify-between pb-1 cursor-pointer select-none group"
                            onClick={() => setIsExpanded(!isExpanded)}
                        >
                            <div className="flex items-center gap-1.5">
                                <IconReceipt size={14} className="text-slate-500 group-hover:text-slate-700 transition-colors" />
                                <span className="text-[10px] font-bold text-gray-500 group-hover:text-gray-700 uppercase tracking-wider transition-colors">
                                    {t('itemized_bookings')}
                                </span>
                            </div>
                            {isExpanded ? (
                                <IconChevronUp size={14} className="text-slate-400 group-hover:text-slate-600 transition-colors" />
                            ) : (
                                <IconChevronDown size={14} className="text-slate-400 group-hover:text-slate-600 transition-colors" />
                            )}
                        </div>

                        {/* 2. Grouped Service Bookings List by Category */}
                        {isExpanded && groupedBookings.length > 0 && (
                            <div className="space-y-1.5">
                                {groupedBookings.map((group) => (
                                    <div key={`${group.categoryKey}_${group.currency}`} className="flex items-center justify-between text-xs min-w-0">
                                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                            <span className="font-semibold text-slate-700 truncate" title={group.categoryLabel}>
                                                {group.categoryLabel}
                                            </span>
                                            {group.totalPax > 0 && (
                                                <span className="text-[10px] text-slate-400 font-medium shrink-0">
                                                    (x{group.totalPax} pax)
                                                </span>
                                            )}
                                        </div>
                                        <span className=" font-semibold text-slate-800 shrink-0 ml-2">
                                            ${formatPrice(group.totalAmount)} <span className="text-[10px] text-slate-400 font-normal uppercase">{group.currency}</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* 3. Subtotal per currency before modifiers (if modifiers exist or multiple categories) */}
                        {isExpanded && modifiers.length > 0 && Object.keys(bookingsSubtotalPerCurrency).length > 0 && (
                            <div className="pt-2 border-t border-dashed border-gray-200 space-y-1">
                                <div className="flex items-center justify-between text-xs text-slate-500">
                                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                                        {t('service_bookings_subtotal')}
                                    </span>
                                    <div className="text-right font-bold text-slate-600">
                                        {Object.entries(bookingsSubtotalPerCurrency).map(([curr, amt]) => (
                                            <div key={curr}>
                                                ${formatPrice(amt)} <span className="text-[10px] font-normal uppercase">{curr}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* 4. Price Modifiers Section */}
                {showModifiers && (modifiers.length > 0 || !disabled) && (
                    <div className={showServiceDetails ? "pt-2 border-t border-dashed border-gray-200" : ""}>
                        {disabled ? (
                            <ReservationPriceModifiersView reservation={reservation} />
                        ) : (
                            <ReservationPriceModifiers
                                reservation={reservation}
                                onOpenExchangeModal={() => setExchangeModalOpened(true)}
                                disabled={disabled}
                            />
                        )}
                    </div>
                )}

                {/* 5. Final Net Totals per Currency */}
                <div>
                    <div className="bg-green-100 rounded-xl px-2 py-1 print:p-2 print:rounded-md flex gap-4 print:gap-2 justify-between">
                        <div className="text-xs print:text-[10px] font-bold text-slate-700 uppercase tracking-wider pt-0.5">
                            {t('total')}
                        </div>
                        <div className="space-y-1 print:space-y-1">
                            {currencies.map((currency, index) => {
                                const total = totalMoneyInByCurrency[currency] !== undefined
                                    ? totalMoneyInByCurrency[currency]
                                    : (bookingsSubtotalPerCurrency[currency] || 0);
                                const remaining = owedByCurrency[currency] !== undefined ? owedByCurrency[currency]! : total;
                                const paid = Math.max(0, total - remaining);

                                return (
                                    <div key={currency} className="space-y-1 print:space-y-0.5">
                                        <div className="flex items-baseline justify-end gap-1.5 font-bold text-slate-900 whitespace-nowrap">
                                            <span className="font-light text-slate-400 text-sm print:text-xs">$</span>
                                            <span className="text-xl print:text-sm tracking-tight font-extrabold">{formatPrice(total)}</span>
                                            <span className="text-xs print:text-[10px] text-slate-600 font-semibold uppercase ml-0.5">{currency}</span>
                                        </div>

                                        {status === 'partially_paid' && (
                                            <div className="space-y-1 print:space-y-0.5 pt-1.5 print:pt-0.5">
                                                <div className="flex items-center justify-between text-xs print:text-[10px] gap-3 print:gap-1.5 min-w-0">
                                                    <span className="text-[11px] print:text-[9px] text-slate-500 uppercase font-medium tracking-wider shrink-0">
                                                        {t('paid_amount')}
                                                    </span>
                                                    <span className="font-semibold text-emerald-700 whitespace-nowrap">
                                                        ${formatPrice(paid)} <span className="text-[10px] print:text-[9px] text-slate-400 font-normal uppercase">{currency}</span>
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between text-xs print:text-[10px] gap-3 print:gap-1.5 min-w-0">
                                                    <span className="text-[11px] print:text-[9px] text-slate-500 uppercase font-medium tracking-wider shrink-0">
                                                        {t('remaining_amount')}
                                                    </span>
                                                    <span className={`font-bold whitespace-nowrap ${remaining > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                                        ${formatPrice(remaining < 0 ? 0 : remaining)} <span className="text-[10px] print:text-[9px] opacity-75 font-normal uppercase">{currency}</span>
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {!disabled && (
                    <ReservationCurrencyExchangeModal
                        reservation={reservation}
                        opened={exchangeModalOpened}
                        onClose={() => setExchangeModalOpened(false)}
                    />
                )}
            </div>
        );
    }

    // Compact Mode (for list cards / table summaries)
    return (
        <div className={className || 'space-y-0.5'}>
            <div className="bg-green-100 border border-green-200/90 rounded-lg px-1">
                <div className="space-y-0.5">
                    {currencies.map((currency, index) => {
                        const total = totalMoneyInByCurrency[currency] !== undefined
                            ? totalMoneyInByCurrency[currency]
                            : (bookingsSubtotalPerCurrency[currency] || 0);
                        const remaining = owedByCurrency[currency] !== undefined ? owedByCurrency[currency]! : total;
                        const paid = Math.max(0, total - remaining);

                        return (
                            <div key={currency}>
                                <div className="flex items-baseline justify-between gap-2 min-w-0">
                                    <div className="flex items-baseline justify-end gap-1 font-bold text-slate-900 whitespace-nowrap shrink-0 ml-auto">
                                        <span className="font-light text-slate-400">$</span>
                                        <span className="text-sm tracking-tight font-extrabold">{formatPrice(total)}</span>
                                        <span className="text-[11px] font-semibold text-slate-500 uppercase ml-0.5">{currency}</span>
                                    </div>
                                </div>

                                {status === 'partially_paid' && (
                                    <div className="border-t border-slate-200/80 space-y-0.5">
                                        <div className="flex items-center justify-between text-xs gap-2 min-w-0">
                                            <span className="text-[10px] text-slate-500 uppercase font-medium tracking-wider shrink-0">
                                                {t('paid_amount')}
                                            </span>
                                            <span className="font-semibold text-emerald-700 whitespace-nowrap">
                                                ${formatPrice(paid)} <span className="text-[10px] text-slate-400 font-normal uppercase">{currency}</span>
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between text-xs gap-2 min-w-0">
                                            <span className="text-[10px] text-slate-500 uppercase font-medium tracking-wider shrink-0">
                                                {t('remaining_amount')}
                                            </span>
                                            <span className={`font-bold whitespace-nowrap ${remaining > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                                ${formatPrice(remaining < 0 ? 0 : remaining)} <span className="text-[10px] opacity-75 font-normal uppercase">{currency}</span>
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {!disabled && (
                <ReservationCurrencyExchangeModal
                    reservation={reservation}
                    opened={exchangeModalOpened}
                    onClose={() => setExchangeModalOpened(false)}
                />
            )}
        </div>
    );
}
